import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { Dialog, Icon, TextField, Layout, Field, Banner, Text } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'
import { transformValidationErrors } from 'core/utils/yup'

import { type EffectiveCarrier } from '../../domain/Shipment'
import { useSaveAssetCarrier } from '../hooks/useSaveAssetCarrier'

type AssetCarrierDialogProps = {
  readonly open: boolean
  readonly shipmentUUID: string
  readonly effectiveCarrier?: EffectiveCarrier
  readonly onClose: () => void
  readonly onAssetCarrierAssigned: () => void
}

type FormErrors = {
  name?: string
  dot?: string
  mc?: string
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Required').max(255, 'Must be 255 characters or less'),
  dot: Yup.string().max(20, 'Must be 20 characters or less'),
  mc: Yup.string().max(20, 'Must be 20 characters or less'),
})

function getFieldStatus(error?: string) {
  return error ? 'danger' : 'default'
}

const defaultInitialValues = {
  name: '',
}

export function AssetCarrierDialog({
  open,
  shipmentUUID,
  effectiveCarrier,
  onClose,
  onAssetCarrierAssigned,
}: AssetCarrierDialogProps) {
  const [assetCarrier, setAssetCarrier] = useState<EffectiveCarrier>(
    effectiveCarrier || defaultInitialValues
  )
  const [errors, setErrors] = useState<FormErrors>({})
  const { saveAssetCarrier, requestStatus } = useSaveAssetCarrier()

  useEffect(() => {
    if (requestStatus === RequestStatus.Error) {
      toast.error('Failed to assign the asset carrier')
    }
  }, [requestStatus])

  useEffect(() => {
    if (requestStatus === RequestStatus.Done) {
      toast.success('The asset carrier has been successfully assigned')
      onAssetCarrierAssigned()
      onClose()
    }
  }, [onAssetCarrierAssigned, onClose, requestStatus])

  async function handleSave() {
    setErrors({})

    try {
      validationSchema.validateSync(assetCarrier, { abortEarly: false })

      saveAssetCarrier({
        isExistingAsset: effectiveCarrier != null,
        shipmentUUID,
        effectiveCarrier: assetCarrier,
      })
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        setErrors(transformValidationErrors(error))
      }
    }
  }

  return createPortal(
    <Dialog open={open} data-testid="assign-asset-carrier-dialog" onClose={onClose}>
      <Dialog.Header>
        Inform the asset carrier
        <Dialog.Close data-testid="assign-asset-carrier-dialog-close" onClick={onClose} />
      </Dialog.Header>
      <Dialog.Body>
        <Layout.Stack gap="spacing-1">
          <Layout.Stack gap="spacing-4">
            {requestStatus === RequestStatus.Error && (
              <Banner variant="danger">
                <Banner.Title>Failed to assign the asset carrier</Banner.Title>
                <Banner.Description>
                  Please review the information below and try again.
                </Banner.Description>
                <Banner.Actions>
                  <Banner.ActionPrimary onClick={handleSave}>Retry</Banner.ActionPrimary>
                </Banner.Actions>
              </Banner>
            )}

            <Field status={getFieldStatus(errors.name)} required>
              <Field.Label id="carrier-name-label">Asset carrier name</Field.Label>
              <TextField
                aria-labelledby="carrier-name-label"
                placeholder="e.g. Jack Smith"
                value={assetCarrier.name}
                onInput={event => {
                  const name = (event.target as HTMLInputElement).value
                  setAssetCarrier({ ...assetCarrier, name })
                }}
              />
              <Field.Hint>{errors.name}</Field.Hint>
            </Field>
            <Layout.Grid>
              <Field status={getFieldStatus(errors.dot)}>
                <Field.Label id="dot-label">DOT</Field.Label>
                <TextField
                  aria-labelledby="dot-label"
                  placeholder="Enter the number"
                  value={assetCarrier.dot}
                  onInput={event => {
                    const dot = (event.target as HTMLInputElement).value
                    setAssetCarrier({ ...assetCarrier, dot })
                  }}
                />
                <Field.Hint>{errors.dot}</Field.Hint>
              </Field>
              <Field status={getFieldStatus(errors.mc)}>
                <Field.Label id="mc-number-label">MC number</Field.Label>
                <TextField
                  aria-labelledby="mc-number-label"
                  placeholder="Enter the number"
                  value={assetCarrier.mc}
                  onInput={event => {
                    const mc = (event.target as HTMLInputElement).value
                    setAssetCarrier({ ...assetCarrier, mc })
                  }}
                />
                <Field.Hint>{errors.mc}</Field.Hint>
              </Field>
            </Layout.Grid>
          </Layout.Stack>
          <Text variant="body-sm" color="color-text-tertiary">
            We use the asset carrier's DOT and MC number to enhance our tracking capabilities
          </Text>
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionTertiary
          onClick={onClose}
          leading={<Icon name="back" size="16px" color="color-primary-100" />}
        >
          Back
        </Dialog.ActionTertiary>
        <Dialog.ActionPrimary
          onClick={handleSave}
          loading={requestStatus === RequestStatus.Pending}
        >
          Assign and confirm
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>,
    document.body,
    'assign-asset-carrier-dialog-root'
  )
}
